/**
 * Guide Anchor Registry (frontend-safe)
 *
 * Single source of truth for the contextual-help anchors placed along the
 * onboarding-creation -> publish route. Holds ONLY pure data so it can be
 * imported by both the React UI components and the node/CLI `support-flow`
 * catalog without dragging heavy dependencies into either bundle.
 *
 * The support-flow coverage table (`@modules/support-flow/catalog/guideAnchors`)
 * re-uses these literals and adds the atom binding + fill sequence + integrity
 * checks. See `modules/support-flow/GUIDE_ANCHORS_PLAN.md`.
 *
 * Convention: every anchor's `testId` is `guide-anchor-${guideKey}`.
 */

import { SCREEN_EDITOR_FIELD_GUIDE_DEFINITIONS } from '@modules/reactFlow/config/v2ScreenEditorFieldGuides';

export interface GuideAnchorDefinition {
  /** Firestore `guides` lookup key. Also shown in the empty-state popover until content is authored. */
  guideKey: string;
  /** Stable `data-testid` for the `WithGuideHelp` anchor. Always `guide-anchor-${guideKey}`. */
  testId: string;
}

const anchor = <const T extends string>(guideKey: T) => ({
  guideKey,
  testId: `guide-anchor-${guideKey}`,
});

export const SCREEN_EDITOR_SECTION_GUIDE_ANCHORS = {
  headerBar: anchor('screen-editor-section-header'),
  actionBar: anchor('screen-editor-section-action-bar'),
  backdrop: anchor('screen-editor-section-background'),
  copyBlock: anchor('screen-editor-section-content'),
  basicConfig: anchor('screen-editor-section-basic-config'),
  media: anchor('screen-editor-section-media'),
  options: anchor('screen-editor-section-options'),
  variableBinding: anchor('screen-editor-section-variable-binding'),
  animation: anchor('screen-editor-section-animation'),
  textField: anchor('screen-editor-section-text-field'),
  carousel: anchor('screen-editor-section-carousel'),
  stepper: anchor('screen-editor-section-stepper'),
  roller: anchor('screen-editor-section-roller'),
  embed: anchor('screen-editor-section-embed'),
  paywallHeader: anchor('screen-editor-section-paywall-header'),
  paywallMedia: anchor('screen-editor-section-paywall-media'),
  paywallBody: anchor('screen-editor-section-paywall-body'),
  paywallSubscriptions: anchor('screen-editor-section-paywall-subscriptions'),
  paywallFooter: anchor('screen-editor-section-paywall-footer'),
  flexibleSections: anchor('screen-editor-section-flexible-sections'),
  stickyContainer: anchor('screen-editor-section-sticky-container'),
} as const satisfies Record<string, GuideAnchorDefinition>;

export const MAIN_FLOW_SETUP_GUIDE_ANCHORS = {
  customDomainSection: anchor('integrations-custom-domain-section'),
  customDomainInput: anchor('custom-domain-domain-input'),
  customDomainDnsSetup: anchor('custom-domain-dns-setup'),
  customDomainVerification: anchor('custom-domain-verification'),
  stripeConnectSection: anchor('integrations-stripe-connect-section'),
  stripeConnectTestMode: anchor('stripe-connect-test-mode'),
  stripeConnectLiveMode: anchor('stripe-connect-live-mode'),
  stripeConnectOauthGuidance: anchor('stripe-connect-oauth-guidance'),
  analyticsSection: anchor('integrations-analytics-section'),
  analyticsIntegrationList: anchor('analytics-integration-list'),
  analyticsAddProvider: anchor('analytics-add-provider'),
  analyticsProviderConfig: anchor('analytics-provider-config'),
  paywallProductsList: anchor('paywall-products-list'),
  paywallProductsModeTabs: anchor('paywall-products-mode-tabs'),
  paywallProductBasicFields: anchor('paywall-product-basic-fields'),
  paywallProductStripeMapping: anchor('paywall-product-stripe-mapping'),
  paywallProductPriceSetup: anchor('paywall-product-price-setup'),
  paywallProductSubscriptionOptions: anchor('paywall-product-subscription-options'),
} as const satisfies Record<string, GuideAnchorDefinition>;

export const GUIDE_ANCHORS = {
  onboardingListCreate: anchor('onboarding-list-create'),
  onboardingWizardBasicInfo: anchor('onboarding-wizard-basic-info'),
  onboardingWizardThemeSelect: anchor('onboarding-wizard-theme-select'),
  onboardingWizardThemeCustomize: anchor('onboarding-wizard-theme-customize'),
  ...MAIN_FLOW_SETUP_GUIDE_ANCHORS,
  canvasOverview: anchor('canvas-overview'),
  canvasAddScreen: anchor('canvas-add-screen'),
  canvasConnectScreens: anchor('canvas-connect-screens'),
  screenEditorOverview: anchor('screen-editor-overview'),
  screenEditorActionButton: anchor('screen-editor-action-button'),
  ...SCREEN_EDITOR_SECTION_GUIDE_ANCHORS,
  webplacementEntryRules: anchor('webplacement-entry-rules'),
  webplacementStripeMetadata: anchor('webplacement-stripe-metadata'),
  webplacementPublishUrl: anchor('webplacement-publish-url'),
  /** Canvas "Deploy Web" button — the class-C landing target of the activation checklist (DC-18, DC-22). */
  deployWeb: anchor('deploy-web'),
} as const satisfies Record<string, GuideAnchorDefinition>;

/**
 * The ten steps of the create-strategy wizard (W12 Part B, Task B8). One anchor
 * per step, in rail order; the key is also the `guideKey` each `WizardStepDef`
 * carries (`@modules/strategy-blocks/components/strategyWizard/wizardSteps`),
 * so the ⓘ of a step resolves the guide document of the same name. The DEV
 * `guides` documents are seeded by `scripts/guides/seed-strategy-wizard-guides.mjs`.
 *
 * Deliberately NOT spread into `GUIDE_ANCHORS`: that constant feeds
 * `GUIDE_ANCHOR_REGISTRY`, which the support-flow catalog binds one-to-one to
 * the onboarding-creation → publish ROUTE (atom binding, fill sequence,
 * authored guide content, import manifest — all asserted for parity). The
 * create-strategy wizard is a different journey with no atom on that route, so
 * it registers here instead. `GuideAnchorKey`, `getGuideAnchor`,
 * `guideAnchorTestId` and `resolveGuideAnchorKey` cover both sets.
 */
export const STRATEGY_WIZARD_GUIDE_ANCHORS = {
  strategyWizardAnalysis: anchor('strategy-wizard-analysis'),
  strategyWizardTasks: anchor('strategy-wizard-tasks'),
  strategyWizardInsights: anchor('strategy-wizard-insights'),
  strategyWizardInsightVariables: anchor('strategy-wizard-insight-variables'),
  strategyWizardProjectVariables: anchor('strategy-wizard-project-variables'),
  strategyWizardSocialProof: anchor('strategy-wizard-social-proof'),
  strategyWizardDetailsTone: anchor('strategy-wizard-details-tone'),
  strategyWizardBlockTypes: anchor('strategy-wizard-block-types'),
  strategyWizardLibrary: anchor('strategy-wizard-library'),
  strategyWizardAiSettings: anchor('strategy-wizard-ai-settings'),
  strategyWizardReview: anchor('strategy-wizard-review'),
} as const satisfies Record<string, GuideAnchorDefinition>;

/**
 * Parameterised anchors (contracts §6). Their key space is open, so they are not
 * members of GUIDE_ANCHORS / GuideAnchorKey and never appear in the coverage
 * table; `resolveGuideAnchorKey` recognises their shapes for `?guide=`.
 */
export const DYNAMIC_GUIDE_ANCHORS = {
  analyticsCard: (platform: string) => anchor(`analytics-card-${platform}`),
  conditionRow: (screenId: string, idx: number) => anchor(`condition-row-${screenId}-${idx}`),
} as const;

const DYNAMIC_GUIDE_KEY_PATTERNS: readonly RegExp[] = [
  /^analytics-card-[a-z0-9_]+$/,
  /^condition-row-[A-Za-z0-9_-]+-\d+$/,
];

/**
 * Per-field Screen Editor guide anchors, derived from the field-guide catalog
 * (one anchor per documented editor field). Kept OUT of GUIDE_ANCHORS so the
 * `GuideAnchorKey` literal union stays the curated journey set; field keys are
 * plain strings (matching `FieldConfig.guideKey`) and live only in the runtime
 * registry array consumed by the support-flow validators and Firestore lookups.
 */
export const SCREEN_EDITOR_FIELD_GUIDE_ANCHORS: GuideAnchorDefinition[] =
  SCREEN_EDITOR_FIELD_GUIDE_DEFINITIONS.map(definition => anchor(definition.guideKey));

/**
 * The support-flow ROUTE registry: every anchor the coverage table binds to an
 * atom. Off-route anchor sets (`STRATEGY_WIZARD_GUIDE_ANCHORS`) are resolvable
 * but not part of it — see the comment on that constant.
 */
export const GUIDE_ANCHOR_REGISTRY = [
  ...Object.values(GUIDE_ANCHORS),
  ...SCREEN_EDITOR_FIELD_GUIDE_ANCHORS,
];

/** Every registered anchor, route and off-route — what `?guide=` resolves against. */
export const ALL_GUIDE_ANCHOR_DEFINITIONS: GuideAnchorDefinition[] = [
  ...GUIDE_ANCHOR_REGISTRY,
  ...Object.values(STRATEGY_WIZARD_GUIDE_ANCHORS),
];

/** Union of valid guide anchor keys (compile-time guard against typos at call sites). */
export type GuideAnchorKey =
  | (typeof GUIDE_ANCHORS)[keyof typeof GUIDE_ANCHORS]['guideKey']
  | StrategyWizardGuideAnchorKey;

/** The ten create-strategy wizard step keys. */
export type StrategyWizardGuideAnchorKey =
  (typeof STRATEGY_WIZARD_GUIDE_ANCHORS)[keyof typeof STRATEGY_WIZARD_GUIDE_ANCHORS]['guideKey'];

const REGISTRY_BY_KEY = new Map<string, GuideAnchorDefinition>(
  ALL_GUIDE_ANCHOR_DEFINITIONS.map(definition => [definition.guideKey, definition]),
);

/** Resolve a registered anchor definition. Throws on an unknown key. */
export function getGuideAnchor(guideKey: GuideAnchorKey): GuideAnchorDefinition {
  const definition = REGISTRY_BY_KEY.get(guideKey);
  if (!definition) {
    throw new Error(`Unknown guide anchor key "${guideKey}".`);
  }
  return definition;
}

/** Resolve the `data-testid` for a registered anchor. */
export function guideAnchorTestId(guideKey: GuideAnchorKey): string {
  return getGuideAnchor(guideKey).testId;
}

/**
 * Resolve a raw `?guide=` value. Static keys resolve from the registry; dynamic
 * keys resolve by shape. Unknown, empty, or camelCase values resolve to null so
 * the caller can no-op (AC-047).
 */
export function resolveGuideAnchorKey(raw: string | null | undefined): GuideAnchorDefinition | null {
  const key = typeof raw === 'string' ? raw.trim() : '';
  if (!key) return null;
  const known = REGISTRY_BY_KEY.get(key);
  if (known) return known;
  return DYNAMIC_GUIDE_KEY_PATTERNS.some((pattern) => pattern.test(key)) ? anchor(key) : null;
}

/**
 * Page-landing redirects for anchors that no longer render on the page a `?guide=`
 * link lands on (D-23, plan modules/navigation/plans/2026-10-07-sidebar-subscription-menu-plan.md).
 * The three Custom Domain workflow anchors render ONLY inside the setup dialog
 * (`DomainSetupForm`, closed on landing); their guide content is merged into the
 * section guide, so a landing for one of them highlights the "Custom Domain" section
 * help icon. Registry keys, testids and the in-dialog anchors are unchanged.
 */
export const GUIDE_ANCHOR_LANDING_REDIRECTS: Readonly<Record<string, GuideAnchorDefinition>> = {
  [MAIN_FLOW_SETUP_GUIDE_ANCHORS.customDomainInput.guideKey]: MAIN_FLOW_SETUP_GUIDE_ANCHORS.customDomainSection,
  [MAIN_FLOW_SETUP_GUIDE_ANCHORS.customDomainDnsSetup.guideKey]: MAIN_FLOW_SETUP_GUIDE_ANCHORS.customDomainSection,
  [MAIN_FLOW_SETUP_GUIDE_ANCHORS.customDomainVerification.guideKey]: MAIN_FLOW_SETUP_GUIDE_ANCHORS.customDomainSection,
};

/**
 * Resolve a raw `?guide=` value to the anchor to highlight on page landing:
 * `resolveGuideAnchorKey` plus `GUIDE_ANCHOR_LANDING_REDIRECTS`.
 */
export function resolveGuideLandingAnchor(raw: string | null | undefined): GuideAnchorDefinition | null {
  const definition = resolveGuideAnchorKey(raw);
  if (!definition) return null;
  return GUIDE_ANCHOR_LANDING_REDIRECTS[definition.guideKey] ?? definition;
}
